import log from 'loglevel';
import { makeAutoObservable } from 'mobx';
import { injectable } from 'tsyringe';

interface Action {
    message?: string;
    isBlocking: boolean;
    id: string;
}

export const TOKEN_RunningActionStore = Symbol("RunningActionStore");

@injectable()
export default class RunningActionStore {
    private _actions: Action[] = [];

    constructor() {
        makeAutoObservable(this);
    }

    public get isRunningBlockingAction(): boolean {
        return this._actions.some(action => action.isBlocking);
    }

    public get isRunningBackgroundAction(): boolean {
        return this._actions.some(action => !action.isBlocking);
    }

    public get message(): string {
        // Get the most recent blocking action message, or if none, the most recent background action
        const blockingAction = [...this._actions].reverse().find(action => action.isBlocking);
        const anyAction = this._actions[this._actions.length - 1];
        return blockingAction?.message || anyAction?.message || '';
    }

    private startAction(isBlocking: boolean, message?: string): () => void {
        const id = crypto.randomUUID();
        log.info(`Starting ${isBlocking ? " blocking " : " background "} action with id '${id}'`)
        this._actions.push({ message, isBlocking, id });

        return () => {
            const index = this._actions.findIndex(action => action.id === id);
            if (index !== -1) {
                this._actions.splice(index, 1);
            }
            log.info(`Completed ${isBlocking ? " blocking " : " background "} action with id ${id}`);
        };
    }

    public manageAction = async <T>(
        action: () => Promise<T>,
        isBlocking: boolean,
        message?: string
    ): Promise<T> => {
        const complete = this.startAction(isBlocking, message);
        try {
            return await action();
        } finally {
            complete();
        }
    }
}
